import { notifications } from '@mantine/notifications';
import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import type { Form, HttpNewForm, VerifiedEmailRow } from '@/api';
import { dumpFormSpecToToml } from '@/lib/form-specs';
import { STARTER_FIELDS, type StarterFieldsId } from '@/lib/form-templates';
import { useRustyState } from '@/state';

export function starterSpecToml(id: StarterFieldsId): string {
  const starter = STARTER_FIELDS.find((s) => s.id === id) ?? STARTER_FIELDS[0];
  return dumpFormSpecToToml(starter.spec);
}

function errorCode(e: unknown): string | undefined {
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  return data && typeof data === 'object' && 'code' in data
    ? String((data as { code: unknown }).code)
    : undefined;
}

/** Attaches a typed address, sending its verification; reports a refusal itself and rethrows. */
export async function addRecipientByEmail(formId: string, email: string, t: TFunction) {
  const { api } = useRustyState.getState();
  try {
    return await api.addFormRecipientByEmail(formId, email, { onSuccess: false, onError: false });
  } catch (e) {
    notifications.show({
      color: 'red',
      message:
        errorCode(e) === 'email_owned_by_other_account'
          ? t('create.ownedByOther', { email })
          : t('create.recipientFailed', { email }),
    });
    throw e;
  }
}

/** One create request, then the new addresses; returns the form even when an address is refused. */
export async function createForm(
  newForm: HttpNewForm,
  newAddresses: string[],
  t: TFunction
): Promise<Form> {
  const state = useRustyState.getState();
  const form = await state.api.newForm(newForm, { onSuccess: false });
  for (const email of newAddresses) {
    try {
      await addRecipientByEmail(form.id, email, t);
    } catch {
      // already reported; the overview shows the form without that address
    }
  }
  void state.refreshFormsSummary();
  void state.refreshProblemsCount();
  void state.getAndSetVerifiedEmails().catch(() => undefined);
  return form;
}

/** The account's notification addresses, loaded on mount and every 5 s while one is pending. */
export function useAddressPool(): { pool: VerifiedEmailRow[]; loaded: boolean } {
  const pool = useRustyState((s) => s.verifiedEmails);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    useRustyState
      .getState()
      .getAndSetVerifiedEmails()
      .catch(() => undefined)
      .finally(() => setLoaded(true));
  }, []);

  const pending = pool.some((e) => !e.is_verified);
  useEffect(() => {
    if (!pending) return;
    const timer = window.setInterval(() => {
      useRustyState
        .getState()
        .getAndSetVerifiedEmails()
        .catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [pending]);

  return { pool, loaded };
}

/** The address new forms notify by default: the verified account email, else any verified one. */
export function defaultRecipient(pool: VerifiedEmailRow[]): VerifiedEmailRow | undefined {
  return pool.find((e) => e.is_account_email && e.is_verified) ?? pool.find((e) => e.is_verified);
}
